/**
 * Compression d'image CÔTÉ CLIENT (navigateur, via <canvas>) — sans dépendance.
 *
 * Pourquoi : les images de simulateur sont stockées en base64 dans la colonne
 * `simulators.image` (text) puis servies par `GET /api/simulators/:id/image`.
 * Historiquement, l'upload admin stockait le fichier brut tel quel : une image
 * de 1696×1699 px (7,6 Mo) était servie pour un affichage de ~160 px max
 * → alerte Lighthouse « Properly size images » + DB gonflée.
 *
 * Ce module redimensionne et ré-encode l'image AU MOMENT DE L'UPLOAD, dans le
 * navigateur. C'est donc CE base64 réduit qui part au serveur et finit en base.
 * Aucune lib native serveur (type `sharp`, lourde à builder sur Scalingo).
 *
 * Stratégie :
 *  - on charge le fichier dans un <img> ;
 *  - on le redimensionne à MAX_DIM (côté le plus long), ratio préservé ;
 *  - on ré-encode en WebP (qualité ~0.85), avec fallback JPEG si le navigateur
 *    ne sait pas produire de WebP (canvas.toDataURL renvoie alors du PNG) ;
 *  - on renvoie le data URL. En cas d'échec (SVG, GIF animé, fichier illisible…),
 *    on renvoie le data URL d'origine pour ne jamais bloquer l'upload.
 */

/** Côté le plus long, en pixels. Les images sont affichées à ≤ 160 px ; 512 laisse une marge HiDPI confortable. */
export const MAX_DIM = 512;
/** Qualité d'encodage WebP/JPEG (0–1). 0.85 = bon compromis poids/qualité pour un avatar. */
export const DEFAULT_QUALITY = 0.85;

export interface CompressOptions {
  maxDim?: number;
  quality?: number;
}

/**
 * Lit un Blob en data URL base64 (Promise).
 * Utilise FileReader dans le navigateur ; retombe sur arrayBuffer()+base64
 * hors DOM (SSR/test bun) où FileReader n'existe pas.
 */
async function readFileAsDataUrl(file: Blob): Promise<string> {
  if (typeof FileReader !== "undefined") {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error ?? new Error("FileReader error"));
      reader.readAsDataURL(file);
    });
  }
  const buf = Buffer.from(await file.arrayBuffer());
  const type = (file as File).type || "application/octet-stream";
  return `data:${type};base64,${buf.toString("base64")}`;
}

/** Charge un data URL dans un HTMLImageElement décodé (Promise). */
function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Image decode error"));
    img.src = dataUrl;
  });
}

/** Calcule les dimensions cibles en préservant le ratio, plafonnées à maxDim. */
export function fitWithin(
  width: number,
  height: number,
  maxDim: number,
): { width: number; height: number } {
  if (width <= 0 || height <= 0) return { width: 0, height: 0 };
  const longest = Math.max(width, height);
  if (longest <= maxDim) {
    return { width: Math.round(width), height: Math.round(height) };
  }
  const scale = maxDim / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/**
 * Compresse un fichier image en un data URL réduit (WebP, fallback JPEG/PNG).
 * Ne jette jamais : en cas de souci, renvoie le data URL d'origine inchangé.
 */
export async function compressImageFile(
  file: Blob,
  opts: CompressOptions = {},
): Promise<string> {
  const maxDim = opts.maxDim ?? MAX_DIM;
  const quality = opts.quality ?? DEFAULT_QUALITY;

  const originalDataUrl = await readFileAsDataUrl(file);

  // Hors navigateur (SSR/test sans DOM) ou type non rasterisable → on ne touche pas.
  if (typeof document === "undefined") return originalDataUrl;
  // SVG : pas de bénéfice à rasteriser, et risque de perte de netteté → on garde.
  const fileType = (file as File).type ?? "";
  if (fileType === "image/svg+xml") return originalDataUrl;

  try {
    const img = await loadImage(originalDataUrl);
    const { width, height } = fitWithin(
      img.naturalWidth || img.width,
      img.naturalHeight || img.height,
      maxDim,
    );
    if (!width || !height) return originalDataUrl;

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return originalDataUrl;
    ctx.drawImage(img, 0, 0, width, height);

    // WebP d'abord ; si non supporté, le navigateur renvoie un data URL PNG
    // (le préfixe ne sera pas "data:image/webp"). On bascule alors sur JPEG.
    let out = canvas.toDataURL("image/webp", quality);
    if (!out.startsWith("data:image/webp")) {
      out = canvas.toDataURL("image/jpeg", quality);
    }

    // Garde-fou : si pour une raison quelconque le résultat est plus lourd que
    // l'original (petite image déjà optimisée), on garde l'original.
    if (out.length >= originalDataUrl.length) return originalDataUrl;
    return out;
  } catch {
    return originalDataUrl;
  }
}
